import React, { useState, useEffect } from "react";
import axios from "axios";

const API = process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5000`;

// ─── Forgot Password Modal (4 steps) ──────────────────────
const ForgotPasswordModal = ({ onClose }) => {
  const [step, setStep] = useState(1); // 1=email, 2=otp, 3=newpw, 4=success
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);

  const startCooldown = () => {
    setResendCooldown(60);
    const t = setInterval(() => {
      setResendCooldown((c) => { if (c <= 1) { clearInterval(t); return 0; } return c - 1; });
    }, 1000);
  };

  const sendOtp = async () => {
    setLoading(true); setError("");
    try {
      await axios.post("/api/auth/forgot-password", { email });
      setStep(2);
      startCooldown();
    } catch (e) {
      setError(e.response?.data?.message || "Failed to send code.");
    } finally { setLoading(false); }
  };

  const verifyOtp = async () => {
    setLoading(true); setError("");
    try {
      await axios.post("/api/auth/verify-otp", { email, otp });
      setStep(3);
    } catch (e) {
      setError(e.response?.data?.message || "Invalid code.");
    } finally { setLoading(false); }
  };

  const resetPassword = async () => {
    if (newPassword !== confirmPassword) { setError("Passwords do not match."); return; }
    if (newPassword.length < 6) { setError("Password must be at least 6 characters."); return; }
    setLoading(true); setError("");
    try {
      await axios.post("/api/auth/reset-password", { email, otp, newPassword });
      setStep(4);
    } catch (e) {
      setError(e.response?.data?.message || "Reset failed.");
    } finally { setLoading(false); }
  };

  const inputCls = "w-full px-5 py-4 border-2 border-purple-200 rounded-2xl focus:ring-4 focus:ring-purple-500/20 focus:border-purple-500 bg-purple-50/60 text-base placeholder-purple-300 font-medium transition-all outline-none";
  const btnCls = "w-full py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold text-lg rounded-2xl shadow-lg hover:shadow-purple-400/40 hover:scale-[1.02] transition-all disabled:opacity-50 disabled:cursor-not-allowed";

  const steps = ["Email", "Verify Code", "New Password", "Done"];

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-8 relative" onClick={(e) => e.stopPropagation()}>
        {/* Close */}
        <button onClick={onClose} className="absolute top-5 right-5 p-2 rounded-xl hover:bg-purple-50 text-gray-400 hover:text-purple-600 transition-all">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>
        </button>

        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl mx-auto mb-4 flex items-center justify-center shadow-lg">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"/></svg>
          </div>
          <h2 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">Reset Password</h2>
        </div>

        {/* Step Indicator */}
        <div className="flex items-center justify-center mb-6 gap-1">
          {steps.map((s, i) => (
            <React.Fragment key={i}>
              <div className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold transition-all ${step > i + 1 ? "bg-purple-500 text-white" : step === i + 1 ? "bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-md" : "bg-gray-100 text-gray-400"}`}>
                {step > i + 1 ? "✓" : i + 1}
              </div>
              {i < steps.length - 1 && <div className={`flex-1 h-0.5 max-w-[32px] rounded ${step > i + 1 ? "bg-purple-400" : "bg-gray-200"}`}/>}
            </React.Fragment>
          ))}
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm font-medium flex items-center gap-2">
            <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/></svg>
            {error}
          </div>
        )}

        {/* Step 1: Email */}
        {step === 1 && (
          <div className="space-y-4">
            <p className="text-gray-500 text-sm text-center">Enter your registered email address and we'll send you a 6-digit reset code.</p>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address</label>
              <input type="email" value={email} onChange={(e) => { setEmail(e.target.value); setError(""); }}
                className={inputCls} placeholder="your@email.com" onKeyDown={(e) => e.key === "Enter" && email && sendOtp()}/>
            </div>
            <button onClick={sendOtp} disabled={!email || loading} className={btnCls}>
              {loading ? "Sending..." : "Send Reset Code"}
            </button>
          </div>
        )}

        {/* Step 2: OTP */}
        {step === 2 && (
          <div className="space-y-4">
            <p className="text-gray-500 text-sm text-center">We sent a 6-digit code to <span className="font-semibold text-purple-600">{email}</span>. Check your inbox.</p>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Verification Code</label>
              <input type="text" value={otp} onChange={(e) => { setOtp(e.target.value.replace(/\D/g, "").slice(0, 6)); setError(""); }}
                className={`${inputCls} text-center text-2xl font-bold tracking-[0.5em]`} placeholder="------" maxLength={6}
                onKeyDown={(e) => e.key === "Enter" && otp.length === 6 && verifyOtp()}/>
            </div>
            <button onClick={verifyOtp} disabled={otp.length !== 6 || loading} className={btnCls}>
              {loading ? "Verifying..." : "Verify Code"}
            </button>
            <div className="text-center">
              {resendCooldown > 0 ? (
                <p className="text-sm text-gray-400">Resend in <span className="font-semibold text-purple-500">{resendCooldown}s</span></p>
              ) : (
                <button onClick={sendOtp} disabled={loading} className="text-sm font-semibold text-purple-600 hover:underline">Resend Code</button>
              )}
            </div>
          </div>
        )}

        {/* Step 3: New Password */}
        {step === 3 && (
          <div className="space-y-4">
            <p className="text-gray-500 text-sm text-center">Create a strong new password (min. 6 characters).</p>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">New Password</label>
              <div className="relative">
                <input type={showPw ? "text" : "password"} value={newPassword}
                  onChange={(e) => { setNewPassword(e.target.value); setError(""); }}
                  className={`${inputCls} pr-12`} placeholder="New password"/>
                <button type="button" onClick={() => setShowPw(!showPw)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-purple-400 hover:text-purple-600">
                  {showPw ? <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"/></svg>
                  : <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Confirm Password</label>
              <div className="relative">
                <input type={showConfirm ? "text" : "password"} value={confirmPassword}
                  onChange={(e) => { setConfirmPassword(e.target.value); setError(""); }}
                  className={`${inputCls} pr-12`} placeholder="Confirm password"
                  onKeyDown={(e) => e.key === "Enter" && newPassword && confirmPassword && resetPassword()}/>
                <button type="button" onClick={() => setShowConfirm(!showConfirm)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-purple-400 hover:text-purple-600">
                  {showConfirm ? <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"/></svg>
                  : <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>}
                </button>
              </div>
            </div>
            {newPassword && confirmPassword && newPassword === confirmPassword && (
              <p className="text-green-600 text-xs font-medium flex items-center gap-1">✓ Passwords match</p>
            )}
            <button onClick={resetPassword} disabled={!newPassword || !confirmPassword || loading} className={btnCls}>
              {loading ? "Resetting..." : "Reset Password"}
            </button>
          </div>
        )}

        {/* Step 4: Success */}
        {step === 4 && (
          <div className="text-center space-y-4 py-4">
            <div className="w-20 h-20 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full mx-auto flex items-center justify-center shadow-lg shadow-green-200">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7"/></svg>
            </div>
            <h3 className="text-xl font-bold text-gray-800">Password Reset!</h3>
            <p className="text-gray-500 text-sm">Your password has been successfully updated. You can now log in with your new password.</p>
            <button onClick={onClose} className={btnCls.replace("from-purple-500 to-pink-500", "from-green-500 to-emerald-600").replace("purple-400/40", "green-400/40")}>
              Back to Login
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Unverified Email Modal ─────────────────────────────────
const UnverifiedEmailModal = ({ email, onClose, onVerified }) => {
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const verifyOtp = async () => {
    if (!otp.trim()) { setError("Please enter the verification code"); return; }
    setLoading(true); setError("");
    try {
      const res = await axios.post(`${API}/api/auth/verify-email`, { email, otp });
      if (res.data.success) {
        onVerified(res.data);
      }
    } catch (e) {
      setError(e.response?.data?.message || "Verification failed");
    } finally { setLoading(false); }
  };

  const resendOtp = async () => {
    setLoading(true); setError("");
    try {
      await axios.post(`${API}/api/auth/resend-verification`, { email });
      alert("Verification code resent! Please check your email.");
    } catch (e) {
      setError(e.response?.data?.message || "Failed to resend code");
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-8 relative text-center" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-5 right-5 p-2 rounded-xl hover:bg-purple-50 text-gray-400 hover:text-purple-600 transition-all">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>
        </button>

        <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4 text-3xl">✉️</div>
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Verify Your Email</h2>
        <p className="text-gray-500 text-sm mb-6">Please enter the 6-digit verification code sent to <br/><span className="font-bold text-purple-600">{email}</span></p>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm font-medium flex items-center justify-center gap-2">
            {error}
          </div>
        )}

        <input
          type="text"
          maxLength="6"
          value={otp}
          onChange={(e) => setOtp(e.target.value)}
          className="w-full text-center text-3xl font-black tracking-[0.5em] p-4 bg-gray-50 border-2 border-gray-100 rounded-2xl focus:border-purple-400 focus:bg-white outline-none transition-all mb-6"
          placeholder="------"
        />

        <button
          onClick={verifyOtp}
          disabled={loading}
          className="w-full py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold text-lg rounded-2xl shadow-lg hover:shadow-purple-400/40 hover:-translate-y-1 transition-all disabled:opacity-50"
        >
          {loading ? "Verifying..." : "Verify & Login"}
        </button>

        <p className="text-sm text-gray-500 mt-6">
          Didn't receive the code?{" "}
          <button onClick={resendOtp} disabled={loading} className="text-purple-600 font-bold hover:underline">
            Resend Code
          </button>
        </p>
      </div>
    </div>
  );
};

// ─── Main Login Component ──────────────────────────────────
const Login = ({ isOpen, onClose, setUser, setActiveTab, openAdminLogin }) => {
  const [formData, setFormData] = useState({ emailOrPhone: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isStaffOrAdminError, setIsStaffOrAdminError] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [showUnverified, setShowUnverified] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState("");

  useEffect(() => {
    if (isOpen) {
      setShowPassword(false);
      setError("");
      setIsStaffOrAdminError(false);
    }
  }, [isOpen]);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError("");
    setIsStaffOrAdminError(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.emailOrPhone || !formData.password) { setError("Please fill all fields"); return; }
    setLoading(true);
    setIsStaffOrAdminError(false);
    try {
      const response = await axios.post("/api/auth/login", formData);
      if (response.data.success) {
        localStorage.setItem("user", JSON.stringify(response.data));
        setUser(response.data);
        onClose();
        if (response.data.role === "admin") setActiveTab("admin");
        else setActiveTab("dashboard");
      }
    } catch (err) {
      const isStaffOrAdmin = err.response?.data?.isStaffOrAdmin;
      const msg = err.response?.data?.message;
      if (msg === "unverified_email") {
        setUnverifiedEmail(formData.emailOrPhone);
        setShowUnverified(true);
      } else if (isStaffOrAdmin) {
        setIsStaffOrAdminError(true);
        setError(msg || "Staff and Admin accounts must log in via the Staff/Admin Portal.");
      } else {
        setError(msg || "Login failed. Check your credentials.");
      }
    } finally { setLoading(false); }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
        <div className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl w-full max-w-lg p-8 border border-purple-100 relative mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
          {/* Close Button */}
          <button onClick={onClose} className="absolute top-6 right-6 p-3 bg-white/80 hover:bg-purple-50 rounded-2xl shadow-md transition-all hover:scale-110 text-gray-600 hover:text-purple-600" aria-label="Close">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>
          </button>

          {/* Header */}
          <div className="text-center mb-8 pt-4">
            <div className="w-20 h-20 bg-gradient-to-r from-purple-500 to-pink-500 rounded-3xl mx-auto mb-6 shadow-xl border-4 border-white flex items-center justify-center text-3xl">🐾</div>
            <h2 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-3">Welcome Back</h2>
            <p className="text-gray-600 text-lg md:text-xl">Sign in to your FurpectTails account</p>
          </div>

          {/* Error */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 rounded-2xl text-red-700 text-sm font-medium space-y-3">
              <div className="flex items-center space-x-2">
                <svg className="w-5 h-5 shrink-0 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/></svg>
                <span>{error}</span>
              </div>
              {isStaffOrAdminError && openAdminLogin && (
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    openAdminLogin();
                  }}
                  className="w-full py-2.5 px-4 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-xl text-xs flex items-center justify-center gap-2 shadow-md transition-all"
                >
                  <span>🛡️</span> Open Staff / Admin Portal
                </button>
              )}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">Email or Phone Number</label>
              <div className="relative">
                <input type="text" name="emailOrPhone" value={formData.emailOrPhone} onChange={handleInputChange}
                  className="w-full px-14 py-5 border-2 border-purple-200 rounded-3xl focus:ring-4 focus:ring-purple-500/20 focus:border-purple-500 bg-gradient-to-r from-purple-50 to-pink-50 text-lg placeholder-purple-400 font-medium shadow-sm transition-all hover:shadow-md"
                  placeholder="john@example.com or +639123456789" required/>
                <svg className="absolute left-5 top-1/2 -translate-y-1/2 w-6 h-6 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">Password</label>
              <div className="relative">
                <input type={showPassword ? "text" : "password"} name="password" value={formData.password} onChange={handleInputChange}
                  className="w-full px-5 py-5 pr-16 border-2 border-purple-200 rounded-3xl focus:ring-4 focus:ring-purple-500/20 focus:border-purple-500 bg-gradient-to-r from-purple-50 to-pink-50 text-lg placeholder-purple-400 font-medium shadow-sm transition-all hover:shadow-md"
                  placeholder="Enter your password" required/>
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-5 top-1/2 -translate-y-1/2 p-2 hover:bg-purple-100 rounded-xl transition-all">
                  {showPassword
                    ? <svg className="w-5 h-5 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"/></svg>
                    : <svg className="w-5 h-5 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>}
                </button>
              </div>
            </div>

            <div className="text-right pt-2">
              <button type="button" onClick={() => setShowForgot(true)} className="text-sm font-semibold text-purple-600 hover:text-purple-700 transition-all hover:underline">
                Forgot Password?
              </button>
            </div>

            <button type="submit" disabled={loading}
              className="w-full py-6 px-8 bg-gradient-to-r from-purple-500 via-pink-500 to-purple-600 text-white font-bold text-xl rounded-3xl shadow-2xl hover:shadow-purple-500/50 hover:scale-[1.03] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none">
              {loading ? "Signing In..." : "Sign In Securely"}
            </button>
          </form>

          <div className="relative my-8">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-purple-200"/></div>
            <div className="relative flex justify-center text-sm"><span className="px-4 bg-white/95 text-gray-500 font-medium">or</span></div>
          </div>

          <div className="text-center space-y-4">
            <p className="text-gray-600 text-lg font-medium">New here? <span className="font-bold bg-gradient-to-r from-emerald-500 to-teal-500 bg-clip-text text-transparent">Register now!</span></p>
            <button type="button" onClick={() => { setActiveTab("register"); onClose(); }}
              className="w-full py-5 px-8 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-bold text-lg rounded-3xl shadow-xl hover:shadow-emerald-500/50 hover:scale-105 transition-all duration-300">
              Create an Account
            </button>
          </div>

          {openAdminLogin && (
            <div className="mt-6 pt-4 border-t border-gray-100 text-center">
              <button
                type="button"
                onClick={() => {
                  onClose();
                  openAdminLogin();
                }}
                className="text-xs font-semibold text-gray-400 hover:text-purple-600 transition-colors inline-flex items-center gap-1.5"
              >
                <span>🛡️</span> Staff or Admin Portal
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgot && <ForgotPasswordModal onClose={() => setShowForgot(false)} />}
      
      {/* Unverified Email Modal */}
      {showUnverified && (
        <UnverifiedEmailModal
          email={unverifiedEmail}
          onClose={() => setShowUnverified(false)}
          onVerified={(userData) => {
            localStorage.setItem("user", JSON.stringify(userData));
            setUser(userData);
            setShowUnverified(false);
            onClose();
            setActiveTab("dashboard");
          }}
        />
      )}
    </>
  );
};

export { ForgotPasswordModal };
export default Login;
